import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Supported languages — West African + English + French
export const LANGUAGES = [
  { code: 'en', name: 'English', flag: 'EN' },
  { code: 'fr', name: 'Francais', flag: 'FR' },
  { code: 'ha', name: 'Hausa', flag: 'HA' },
  { code: 'yo', name: 'Yoruba', flag: 'YO' },
  { code: 'tw', name: 'Twi', flag: 'TW' },
];

const resources = {
  en: {
    translation: {
      // Nav
      'nav.dashboard': 'Dashboard',
      'nav.lectures': 'Lectures',
      'nav.ai_tutor': 'AI Tutor',
      'nav.learn': 'Learn',
      'nav.track': 'Track',
      'nav.more': 'More',
      'nav.flashcards': 'Flashcards',
      'nav.quick_review': 'Quick Review',
      'nav.exam_mode': 'Exam Mode',
      'nav.doc_uploads': 'My Files',
      'nav.study_plan': 'AI Study Plan',
      'nav.recommendations': 'AI Recommendations',
      'nav.subjects': 'My Subjects',
      'nav.schedule': 'Study Schedule',
      'nav.progress': 'My Progress',
      'nav.analytics': 'Analytics',
      'nav.achievements': 'Achievements',
      'nav.study_groups': 'Study Groups',
      'nav.referrals': 'Referral Rewards',
      'nav.campus': 'Campus Plans',
      'nav.api_docs': 'API Docs',
      'nav.settings': 'Settings',
      'nav.sign_out': 'Sign Out',
      // Common
      'common.save': 'Save',
      'common.cancel': 'Cancel',
      'common.delete': 'Delete',
      'common.edit': 'Edit',
      'common.loading': 'Loading...',
      'common.search': 'Search',
      'common.back': 'Back',
      'common.next': 'Next',
      'common.done': 'Done',
      'common.skip': 'Skip',
      'common.new_chat': 'New Chat',
      'common.upload': 'Upload',
      'common.export': 'Export',
      // Dashboard
      'dash.welcome': 'Welcome back',
      'dash.study_goal': 'Daily Study Goal',
      'dash.streak': 'Day Streak',
      'dash.total_xp': 'Total XP',
      // Study Buddy
      'buddy.title': 'Study Buddy AI',
      'buddy.placeholder': 'Ask me anything about your studies...',
      'buddy.chat_history': 'Chat History',
      // Lectures
      'lectures.title': 'Lectures',
      'lectures.live_record': 'Live Record',
      'lectures.upload_doc': 'Upload Doc',
      'lectures.new_lecture': 'New Lecture',
      // Exam
      'exam.title': 'Exam Mode',
      'exam.ai_prep': 'AI Prep',
      'exam.question_bank': 'Question Bank',
      'exam.generate': 'Generate Questions',
      'exam.reveal': 'Reveal Answer',
      // Analytics
      'analytics.title': 'Analytics',
      'analytics.study_trend': 'Study Trend',
      'analytics.ai_usage': 'AI Usage',
      'analytics.readiness': 'Exam Readiness',
      'analytics.weak_areas': 'Weak Areas',
      // Schedule
      'schedule.title': 'Study Schedule',
      'schedule.sessions': 'Study Sessions',
      'schedule.exams': 'Exams',
      'schedule.schedule_exam': 'Schedule Exam',
      // Settings
      'settings.light': 'Light',
      'settings.dark': 'Dark',
      'settings.privacy': 'Privacy & Data',
      'settings.export_data': 'Export My Data',
      'settings.export_desc': 'Download all your data as JSON (GDPR)',
      'settings.delete_account': 'Delete Account',
      'settings.delete_desc': 'Permanently delete all your data',
      'settings.language': 'Language',
      'settings.notifications': 'Notifications',
      // New features
      'nav.feed': 'Learning Feed',
      'nav.leaderboards': 'Leaderboards',
      'nav.marketplace': 'Marketplace',
      'nav.tutors': 'Tutor Connect',
      'nav.educators': 'Educators',
      'nav.messages': 'Messages',
      'nav.wallet': 'Wallet',
      'feed.title': 'Learning Feed',
      'feed.for_you': 'For You',
      'feed.trending': 'Trending',
      'feed.following': 'Following',
      'marketplace.title': 'Marketplace',
      'marketplace.sell': 'Sell Notes',
      'marketplace.browse': 'Browse',
      'marketplace.my_listings': 'My Listings',
      'marketplace.purchased': 'Purchased',
      'marketplace.free': 'Free',
      'marketplace.buy': 'Buy',
      'marketplace.get_free': 'Get Free',
      'tutors.title': 'Tutor Connect',
      'tutors.find': 'Find Tutors',
      'tutors.become': 'Become a Tutor',
      'tutors.sessions': 'My Sessions',
      'tutors.book': 'Book Session',
      'tutors.per_hour': '/hr',
      'educators.title': 'Educators',
      'educators.upgrade': 'Become an Educator',
      'educators.my_courses': 'My Courses',
      'educators.publish': 'Publish Material',
      'educators.enroll': 'Enroll',
      'messages.title': 'Messages',
      'messages.new': 'New Message',
      'messages.placeholder': 'Type a message...',
      'messages.online': 'Online',
      'messages.offline': 'Offline',
      'wallet.title': 'Wallet',
      'wallet.balance': 'Available Balance',
      'wallet.withdraw': 'Withdraw',
      'wallet.setup_momo': 'Setup Mobile Money',
      'wallet.history': 'Payout History',
      'leaderboards.title': 'Leaderboards',
      'leaderboards.school': 'My School',
      'leaderboards.global': 'Global',
      'leaderboards.schools': 'Top Schools',
      'challenge.title': 'Quiz Challenge',
      'challenge.accept': 'Accept Challenge',
      'challenge.share_whatsapp': 'Share on WhatsApp',
    }
  },
  fr: {
    translation: {
      'nav.dashboard': 'Tableau de bord',
      'nav.lectures': 'Cours',
      'nav.ai_tutor': 'Tuteur IA',
      'nav.learn': 'Apprendre',
      'nav.track': 'Suivre',
      'nav.more': 'Plus',
      'nav.flashcards': 'Fiches',
      'nav.quick_review': 'Revision rapide',
      'nav.exam_mode': 'Mode Examen',
      'nav.doc_uploads': 'Documents',
      'nav.study_plan': "Plan d'etude IA",
      'nav.recommendations': 'Recommandations IA',
      'nav.subjects': 'Mes matieres',
      'nav.schedule': 'Emploi du temps',
      'nav.progress': 'Mes progres',
      'nav.analytics': 'Analytique',
      'nav.achievements': 'Succes',
      'nav.study_groups': "Groupes d'etude",
      'nav.referrals': 'Parrainage',
      'nav.campus': 'Plans Campus',
      'nav.api_docs': 'Documentation API',
      'nav.settings': 'Parametres',
      'nav.sign_out': 'Deconnexion',
      'common.save': 'Enregistrer',
      'common.cancel': 'Annuler',
      'common.delete': 'Supprimer',
      'common.edit': 'Modifier',
      'common.loading': 'Chargement...',
      'common.search': 'Rechercher',
      'common.back': 'Retour',
      'common.next': 'Suivant',
      'common.done': 'Termine',
      'common.skip': 'Passer',
      'common.new_chat': 'Nouvelle conversation',
      'common.upload': 'Telecharger',
      'common.export': 'Exporter',
      'dash.welcome': 'Bienvenue',
      'dash.study_goal': "Objectif d'etude quotidien",
      'dash.streak': 'Jours consecutifs',
      'dash.total_xp': 'XP total',
      'buddy.title': 'Assistant IA',
      'buddy.placeholder': 'Posez-moi une question sur vos cours...',
      'buddy.chat_history': 'Historique',
      'lectures.title': 'Cours',
      'lectures.live_record': 'Enregistrer',
      'lectures.upload_doc': 'Telecharger doc',
      'lectures.new_lecture': 'Nouveau cours',
      'exam.title': 'Mode Examen',
      'exam.ai_prep': 'Prep IA',
      'exam.question_bank': 'Banque de questions',
      'exam.generate': 'Generer les questions',
      'exam.reveal': 'Montrer la reponse',
      'analytics.title': 'Analytique',
      'analytics.study_trend': "Tendance d'etude",
      'analytics.ai_usage': 'Utilisation IA',
      'analytics.readiness': 'Preparation aux examens',
      'analytics.weak_areas': 'Points faibles',
      'schedule.title': 'Emploi du temps',
      'schedule.sessions': "Sessions d'etude",
      'schedule.exams': 'Examens',
      'schedule.schedule_exam': 'Planifier un examen',
      'settings.light': 'Clair',
      'settings.dark': 'Sombre',
      'settings.privacy': 'Confidentialite et donnees',
      'settings.export_data': 'Exporter mes donnees',
      'settings.export_desc': 'Telecharger toutes vos donnees en JSON (RGPD)',
      'settings.delete_account': 'Supprimer le compte',
      'settings.delete_desc': 'Supprimer definitivement toutes vos donnees',
      'settings.language': 'Langue',
      'settings.notifications': 'Notifications',
      'nav.feed': 'Fil de nouvelles',
      'nav.leaderboards': 'Classements',
      'nav.marketplace': 'Marche',
      'nav.tutors': 'Tutorat',
      'nav.educators': 'Educateurs',
      'nav.messages': 'Messages',
      'nav.wallet': 'Portefeuille',
      'feed.title': 'Fil de nouvelles',
      'feed.for_you': 'Pour vous',
      'feed.trending': 'Tendances',
      'feed.following': 'Abonnements',
      'marketplace.title': 'Marche',
      'marketplace.sell': 'Vendre des notes',
      'marketplace.browse': 'Parcourir',
      'marketplace.my_listings': 'Mes annonces',
      'marketplace.purchased': 'Achats',
      'marketplace.free': 'Gratuit',
      'marketplace.buy': 'Acheter',
      'marketplace.get_free': 'Obtenir gratuitement',
      'tutors.title': 'Tutorat',
      'tutors.find': 'Trouver un tuteur',
      'tutors.become': 'Devenir tuteur',
      'tutors.sessions': 'Mes sessions',
      'tutors.book': 'Reserver une session',
      'tutors.per_hour': '/heure',
      'educators.title': 'Educateurs',
      'educators.upgrade': 'Devenir educateur',
      'educators.my_courses': 'Mes cours',
      'educators.publish': 'Publier du materiel',
      'educators.enroll': "S'inscrire",
      'messages.title': 'Messages',
      'messages.new': 'Nouveau message',
      'messages.placeholder': 'Ecrivez un message...',
      'messages.online': 'En ligne',
      'messages.offline': 'Hors ligne',
      'wallet.title': 'Portefeuille',
      'wallet.balance': 'Solde disponible',
      'wallet.withdraw': 'Retirer',
      'wallet.setup_momo': 'Configurer Mobile Money',
      'wallet.history': 'Historique des paiements',
      'leaderboards.title': 'Classements',
      'leaderboards.school': 'Mon ecole',
      'leaderboards.global': 'Mondial',
      'leaderboards.schools': 'Meilleures ecoles',
      'challenge.title': 'Defi Quiz',
      'challenge.accept': 'Accepter le defi',
      'challenge.share_whatsapp': 'Partager sur WhatsApp',
    }
  },
  ha: {
    translation: {
      'nav.dashboard': 'Babban Shafi',
      'nav.lectures': 'Darussa',
      'nav.ai_tutor': 'Malamin AI',
      'nav.learn': 'Koyo',
      'nav.track': 'Bincike',
      'nav.more': 'Kari',
      'nav.flashcards': 'Katunan Karatu',
      'nav.quick_review': 'Duba Sauri',
      'nav.exam_mode': 'Yanayin Jarrabawa',
      'nav.subjects': 'Darussan Ni',
      'nav.schedule': 'Jadawalin Karatu',
      'nav.progress': 'Ci Gaban Ni',
      'nav.analytics': 'Nazari',
      'nav.achievements': 'Nasarori',
      'nav.settings': 'Saituna',
      'nav.sign_out': 'Fita',
      'common.save': 'Ajiye',
      'common.cancel': 'Soke',
      'common.delete': 'Goge',
      'common.loading': 'Ana lodi...',
      'common.search': 'Bincika',
      'common.back': 'Komawa',
      'common.done': 'An gama',
      'common.new_chat': 'Sabuwar hira',
      'dash.welcome': 'Barka da dawowa',
      'dash.study_goal': 'Burin karatu na yau',
      'buddy.title': 'Abokin Karatu AI',
      'buddy.placeholder': 'Tambaye ni komai game da karatunka...',
      'lectures.title': 'Darussa',
      'lectures.live_record': 'Dauka kai tsaye',
      'exam.title': 'Yanayin Jarrabawa',
      'analytics.title': 'Nazari',
      'settings.language': 'Harshe',
      'settings.light': 'Haske',
      'settings.dark': 'Duhu',
      'nav.feed': 'Labaran Karatu',
      'nav.leaderboards': 'Matsayi',
      'nav.marketplace': 'Kasuwa',
      'nav.tutors': 'Malamai',
      'nav.educators': 'Malamai Manyan',
      'nav.messages': 'Sakonni',
      'nav.wallet': 'Jakar Kudi',
      'marketplace.title': 'Kasuwa',
      'marketplace.sell': 'Sayar da bayanai',
      'marketplace.free': 'Kyauta',
      'tutors.title': 'Malamai',
      'tutors.book': 'Yi rajista',
      'messages.title': 'Sakonni',
      'messages.placeholder': 'Rubuta sako...',
      'wallet.title': 'Jakar Kudi',
      'wallet.withdraw': 'Cire kudi',
      'leaderboards.title': 'Matsayi',
      'challenge.title': 'Kalubale',
      'challenge.accept': 'Karbi kalubale',
    }
  },
  yo: {
    translation: {
      'nav.dashboard': 'Ibi Akoso',
      'nav.lectures': 'Eko',
      'nav.ai_tutor': 'Oluko AI',
      'nav.learn': 'Keko',
      'nav.track': 'Topa',
      'nav.more': 'Diẹ sii',
      'nav.flashcards': 'Kaadi Eko',
      'nav.subjects': 'Awon Eko Mi',
      'nav.schedule': 'Eto Kiko',
      'nav.progress': 'Ilọsiwaju Mi',
      'nav.analytics': 'Itupalẹ',
      'nav.achievements': 'Aseyori',
      'nav.settings': 'Eto',
      'nav.sign_out': 'Jade',
      'common.save': 'Fi pamo',
      'common.cancel': 'Fagilee',
      'common.delete': 'Pa re',
      'common.loading': 'N gbe wole...',
      'common.search': 'Wa',
      'common.back': 'Pada',
      'common.done': 'Ti pari',
      'common.new_chat': 'Ijiroro tuntun',
      'dash.welcome': 'Kaabo pada',
      'dash.study_goal': 'Ero eko ojoojumo',
      'buddy.title': 'Ore Eko AI',
      'buddy.placeholder': 'Beere lọwọ mi ohunkohun nipa eko rẹ...',
      'lectures.title': 'Eko',
      'exam.title': 'Ipo Idanwo',
      'analytics.title': 'Itupalẹ',
      'settings.language': 'Ede',
      'settings.light': 'Imọlẹ',
      'settings.dark': 'Okunkun',
    }
  },
  tw: {
    translation: {
      'nav.dashboard': 'Dashboard',
      'nav.lectures': 'Adesua',
      'nav.ai_tutor': 'AI Kyerɛkyerɛni',
      'nav.learn': 'Sua',
      'nav.track': 'Di akyi',
      'nav.more': 'Nea ɛka',
      'nav.flashcards': 'Adesua Kaad',
      'nav.subjects': 'Me Adesua',
      'nav.schedule': 'Adesua Nhyehyɛe',
      'nav.progress': 'Me Nkɔsoɔ',
      'nav.analytics': 'Nhwehwɛmu',
      'nav.achievements': 'Nneɛma a meyɛe',
      'nav.settings': 'Nhyehyɛe',
      'nav.sign_out': 'Fi adi',
      'common.save': 'Kora so',
      'common.cancel': 'Gyae',
      'common.delete': 'Popa',
      'common.loading': 'Ɛreload...',
      'common.search': 'Hwehwɛ',
      'common.back': 'San',
      'common.done': 'Awie',
      'common.new_chat': 'Nkɔmmɔ foforɔ',
      'dash.welcome': 'Akwaaba',
      'dash.study_goal': 'Dakoraa adesua botae',
      'buddy.title': 'AI Adesua Adamfo',
      'buddy.placeholder': 'Bisa me biribiara fa wo adesua ho...',
      'lectures.title': 'Adesua',
      'exam.title': 'Sɔhwɛ',
      'analytics.title': 'Nhwehwɛmu',
      'settings.language': 'Kasa',
      'settings.light': 'Hann',
      'settings.dark': 'Sum',
      'nav.feed': 'Adesua Nsɛm',
      'nav.leaderboards': 'Nhyehyɛe',
      'nav.marketplace': 'Dwanbea',
      'nav.tutors': 'Akyerɛkyerɛfo',
      'nav.educators': 'Akyerɛkyerɛfo Akɛseɛ',
      'nav.messages': 'Nkasamu',
      'nav.wallet': 'Sika Kotoku',
      'marketplace.title': 'Dwanbea',
      'marketplace.sell': 'Tɔn nsɛm',
      'marketplace.free': 'Kwa',
      'tutors.title': 'Akyerɛkyerɛfo',
      'tutors.book': 'Fa din',
      'messages.title': 'Nkasamu',
      'messages.placeholder': 'Kyerɛw kasamu...',
      'wallet.title': 'Sika Kotoku',
      'wallet.withdraw': 'Yi sika',
      'leaderboards.title': 'Nhyehyɛe',
      'challenge.title': 'Akansi',
      'challenge.accept': 'Gye akansi',
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: LANGUAGES.map(l => l.code),
    debug: false,
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
    react: { useSuspense: false },
  });

export default i18n;
